import { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { api, stylySpizirny, AKCENT as SECTION_COLOR } from '../../lib/spizirnaUi';
import { RefreshCw, SlidersHorizontal, FileText } from 'lucide-react';
import ModulLayout from '../../components/layout/ModulLayout';
import PrehledPanel from './objedname/PrehledPanel';
import SeznamParovani from './objedname/SeznamParovani';
import DetailParovani from './objedname/DetailParovani';
import ModalVarianty from './objedname/ModalVarianty';
import { doSkupiny } from './objedname/skupiny';

/**
 * Mapování Objedname — cizí názvy na náš receptář.
 *
 * Dočasná záložka Receptáře; žije, dokud Objedname nenahradíme vlastním
 * frontendem. Do té doby je to **jediné místo, kde se cizí katalog převádí
 * na náš** a všechno za ním (kuchyň, účtenka, kalkulace, spotřeba) je přesně
 * tak přesné jako tohle.
 *
 * Rozvržení jede podle normy §1: přehled přes celou šířku → výběr zúží
 * seznam a vedle se otevře **detail jako plnohodnotný sloupec**. Zprvu tu
 * byl inspektor podle §1f, jenže ten je na moduly, kde se detail otevírá
 * občas. Tady se jím projde několik set názvů za sebou a na velkém monitoru
 * nemá smysl kvůli tomu překrývat seznam.
 *
 * Panel se statistikami je `panelVzdy`: pokrytí tržby platí bez ohledu na to,
 * jestli je něco vybrané, a nad seznamem by ukrajovalo výšku pokaždé.
 *
 * Přehled se kreslí **po skupinách z Objedname**. Objedname combo nezná
 * a obchází ho přes přídavky, takže „Nápoj výhodně" nebo „Vyberte si svou
 * pizzu" je jediné místo, kde je ta struktura vidět — a rozhoduje se podle
 * ní po deseti položkách naráz, ne po jedné.
 */
export default function MapovaniObjedname() {
    const { theme } = useTheme();
    const isDark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
    const styly = stylySpizirny(isDark);
    const { secondaryColor, mutedColor } = styly;

    const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
    const [polozky, setPolozky] = useState([]);
    const [prehled, setPrehled] = useState(null);
    const [nacitam, setNacitam] = useState(false);
    const [stav, setStav] = useState('ceka');
    const [typ, setTyp] = useState('');
    const [hledani, setHledani] = useState('');
    const [skupina, setSkupina] = useState(null);
    const [vybrany, setVybrany] = useState(null);
    const [variantyOtevreny, setVariantyOtevreny] = useState(false);
    const [hlaska, setHlaska] = useState('');
    const [prepocitavam, setPrepocitavam] = useState(false);

    useEffect(() => {
        const zmer = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', zmer);
        return () => window.removeEventListener('resize', zmer);
    }, []);

    const nacti = useCallback(async () => {
        setNacitam(true);
        try {
            const p = new URLSearchParams({ stav });
            if (typ) p.set('typ', typ);
            if (hledani.trim()) p.set('q', hledani.trim());
            const [data, stat] = await Promise.all([
                api(`/api/objedname/mapping?${p}`),
                api('/api/objedname/mapping/prehled'),
            ]);
            setPolozky(data);
            setPrehled(stat);
        } catch {
            setHlaska('Data se nepodařilo načíst.');
        } finally {
            setNacitam(false);
        }
    }, [stav, typ, hledani]);

    useEffect(() => {
        const t = setTimeout(nacti, hledani ? 300 : 0);
        return () => clearTimeout(t);
    }, [nacti, hledani]);

    /** Řádky, které jsou zrovna vidět — uvnitř vybrané skupiny, nebo všechny. */
    const viditelne = () => (skupina ? polozky.filter((p) => doSkupiny(p) === skupina) : polozky);

    /**
     * Po rozhodnutí řádek ze seznamu zmizí (filtr „čeká") — proto se rovnou
     * vybere následující. Bez toho by po každém potvrzení skočil výběr pryč
     * a člověk by musel myší hledat, kde skončil.
     */
    const dalsiPo = (id) => {
        const v = viditelne();
        const i = v.findIndex((p) => p.id === id);
        return v[i + 1] ?? v[i - 1] ?? null;
    };

    const potvrd = async (radek) => {
        if (!radek?.navrh?.recept) return;
        const dalsi = dalsiPo(radek.id);
        setPolozky((p) => p.filter((x) => x.id !== radek.id));
        setVybrany(stav === 'ceka' ? dalsi : radek);
        try {
            await api(`/api/objedname/mapping/${radek.id}/potvrdit`, 'POST');
        } catch (e) {
            setHlaska(e?.message || 'Potvrzení se nepovedlo.');
        }
        nacti();
    };

    const uloz = async (id, patch) => {
        const dalsi = dalsiPo(id);
        try {
            await api(`/api/objedname/mapping/${id}`, 'PUT', patch);
            setVybrany(stav === 'ceka' ? dalsi : null);
            nacti();
        } catch (e) {
            setHlaska(e?.message || 'Uložení se nepovedlo.');
        }
    };

    const prepocitat = async () => {
        setPrepocitavam(true);
        try {
            const r = await api('/api/objedname/mapping/auto-match', 'POST');
            setHlaska(`Přepočteno ${r.prepocteno} názvů, návrh má ${r.s_navrhem}.`);
            await nacti();
        } catch {
            setHlaska('Přepočet selhal.');
        } finally {
            setPrepocitavam(false);
        }
    };

    // Klávesnice: na několik set názvů je myš ta pomalejší cesta.
    useEffect(() => {
        const klavesa = (e) => {
            if (variantyOtevreny) return;
            const vPoli = ['INPUT', 'TEXTAREA'].includes(e.target?.tagName);
            if (vPoli && e.key !== 'Escape') return;

            if (e.key === 'Escape') { setVybrany(null); return; }

            const v = viditelne();
            if (!v.length) return;

            const i = vybrany ? v.findIndex((p) => p.id === vybrany.id) : -1;
            if (e.key === 'ArrowDown') { e.preventDefault(); setVybrany(v[Math.min(i + 1, v.length - 1)]); }
            if (e.key === 'ArrowUp') { e.preventDefault(); setVybrany(v[Math.max(i - 1, 0)]); }
            if (e.key === 'Enter' && vybrany?.navrh?.recept) { e.preventDefault(); potvrd(vybrany); }
        };
        window.addEventListener('keydown', klavesa);
        return () => window.removeEventListener('keydown', klavesa);
    });

    const akce = (
        <div className="flex items-center gap-1.5 ml-auto">
            <button onClick={() => setVariantyOtevreny(true)}
                className="inline-flex items-center gap-1.5 text-[11.5px] font-bold px-2.5 rounded-lg"
                style={{
                    height: 32, background: 'transparent', cursor: 'pointer',
                    border: `1px solid ${SECTION_COLOR}55`, color: SECTION_COLOR,
                }}>
                <SlidersHorizontal size={12} />
                Slovník variant
                {prehled?.varianty?.blokuje_nazvu > 0 && (
                    <span className="text-[10px] px-1.5 rounded-full font-bold"
                        style={{ background: `${SECTION_COLOR}25` }}>
                        {prehled.varianty.blokuje_nazvu}
                    </span>
                )}
            </button>

            {/* PDF se otevírá odkazem, ne fetchem: stažení si vyřeší prohlížeč
                a nemusíme tahat megabajty do paměti. */}
            <a href="/api/objedname/mapping/pdf"
                className="inline-flex items-center gap-1.5 text-[11.5px] px-2.5 rounded-lg"
                style={{
                    height: 32, textDecoration: 'none',
                    border: `1px solid ${mutedColor}40`, color: secondaryColor,
                }}
                title="Přehled párování do PDF">
                <FileText size={12} />
                PDF
            </a>

            <button onClick={prepocitat} disabled={prepocitavam}
                className="inline-flex items-center gap-1.5 text-[11.5px] px-2.5 rounded-lg"
                style={{
                    height: 32, background: 'transparent', cursor: 'pointer',
                    border: `1px solid ${mutedColor}40`, color: secondaryColor,
                }}
                title="Přepočítat návrhy — pustit po změně slovníku variant nebo receptáře">
                <RefreshCw size={12} className={prepocitavam ? 'animate-spin' : ''} />
                Přepočítat
            </button>
        </div>
    );

    return (
        <>
            {hlaska && (
                <div className="text-xs mb-2 px-1" style={{ color: secondaryColor, flexShrink: 0 }}>
                    {hlaska}
                </div>
            )}

            <ModulLayout
                isMobile={isMobile}
                vybrano={!!vybrany}
                panelVzdy
                barva={SECTION_COLOR}
                isDark={isDark}
                seznam={({ siroky }) => (
                    <SeznamParovani
                        siroky={siroky} polozky={polozky} nacitam={nacitam}
                        styly={styly} isDark={isDark}
                        stav={stav} onStav={setStav} typ={typ} onTyp={setTyp}
                        hledani={hledani} onHledani={setHledani}
                        skupina={skupina} onSkupina={(s) => { setSkupina(s); setVybrany(null); }}
                        vybranyId={vybrany?.id} onVyber={setVybrany}
                        onPotvrdit={potvrd} akce={akce}
                    />
                )}
                detail={() => (
                    <DetailParovani
                        radek={vybrany} isDark={isDark} isMobile={isMobile} styly={styly}
                        onZavrit={() => setVybrany(null)}
                        onUlozit={(patch) => uloz(vybrany.id, patch)}
                        onPotvrdit={() => potvrd(vybrany)}
                    />
                )}
                panel={() => (
                    <PrehledPanel prehled={prehled} styly={styly} isDark={isDark}
                        onOtevritVarianty={() => setVariantyOtevreny(true)} />
                )}
            />

            {variantyOtevreny && (
                <ModalVarianty isDark={isDark} styly={styly}
                    onZavrit={() => { setVariantyOtevreny(false); nacti(); }} />
            )}
        </>
    );
}
